import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  type EntityFilterQuery,
} from '@backstage/catalog-client';
import { useApi } from '@backstage/core-plugin-api';
import {
  CATALOG_FILTER_EXISTS,
  catalogApiRef,
  EntityDisplayName
} from '@backstage/plugin-catalog-react';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { useCallback, useEffect } from 'react';
import useAsync from 'react-use/esm/useAsync';
import { FieldValidation } from '@rjsf/utils';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { EntityPickerUiOptions } from '@backstage/plugin-scaffolder';
import { 
  CBEntityPickerFilterQueryValue,
  CBEntityPickerFilterQuery,
  CBEntityPickerProps
} from './schema';

export const CBEntityPicker = ({
    onChange,
    schema: { title = 'Entity', description = 'An entity from the catalog' },
    required,
    uiSchema,
    rawErrors,
    formData,
    idSchema,
    disabled,
    readonly    
}: FieldExtensionComponentProps<string>) => {

  const autoFill = uiSchema['ui:options']?.autoFill || false;

  const catalogFilter = buildCatalogFilter(uiSchema);

  const catalogApi = useApi(catalogApiRef);
  const { value: entities } = useAsync(async () => {
    const fields = [
      'metadata.name',
      'metadata.namespace',
      'metadata.title',
      'kind',
    ];
      
    const { items } = await catalogApi.getEntities(
      catalogFilter
      ? { filter: catalogFilter, fields }
      : { filter: undefined, fields }
    )

    return items;
  });

  const validEntities = (entities || []).filter(
    (entity): entity is Entity => entity !== null && entity !== undefined
  );

  // Get every entityRef for component options
  const entitiesRefs = validEntities.map(entity => stringifyEntityRef(entity));

  const onSelect = useCallback(
    (_: any, ref: string | Entity | null) => {
      if (typeof ref !== 'string') {
        onChange(ref ? stringifyEntityRef(ref as Entity) : undefined);
      } else {
        let entityRef = ref;
        try {
          entityRef = stringifyEntityRef(parseEntityRef(ref as string));
        } catch (err) {
          // Ignore error
        }
        if (formData !== ref) {
          onChange(entityRef);
        }
      }
    },
    [onChange, formData]
  );

  const selectedEntity = entitiesRefs.includes(formData as string) ? formData : '';

  useEffect(() => {
    if(!autoFill) {
      if (entitiesRefs.length === 1 && selectedEntity === '') {
        onChange(entitiesRefs[0]);
      }
    } else {
      if(selectedEntity === '') {
        const { kind }: any = uiSchema['ui:options']?.catalogFilter;
        if(kind === 'User') {
          const { userEntityRef } = JSON.parse(localStorage.getItem('USER') || '');
          onChange(userEntityRef);
        }
      }
    }
  }, [entitiesRefs, onChange, selectedEntity, autoFill]);

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
    >
      <Autocomplete
        disabled={disabled || readonly || entitiesRefs?.length <= 1}
        id={idSchema?.$id}
        value={selectedEntity}
        onChange={onSelect}
        options={entitiesRefs}
        renderOption={(option) => <EntityDisplayName entityRef={option} />}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }
          return '';
        }}
        autoSelect
        renderInput={(params) => (
          <TextField
            {...params}
            label={title}
            margin="dense"
            helperText={description}
            FormHelperTextProps={{
              margin: 'dense',
              style: { marginLeft: 0 },
            }}
            variant="outlined"
            InputLabelProps={{ required: required }}
            InputProps={{
              ...params.InputProps,
              required: required,
            }}
          />
        )}
      />
    </FormControl>
  );
};

export const CBEntityPickerValidation = (
  value: string,
  validation: FieldValidation,
) => {
    try {
      parseEntityRef(value);
    } catch {
      validation.addError(`${value} is not a valid entity ref`);
    }
};

/**
 * Converts a especial `{exists: true}` value to the `CATALOG_FILTER_EXISTS` symbol.
 *
 * @param value - The value to convert.
 * @returns The converted value.
 */
function convertOpsValues(
  value: Exclude<CBEntityPickerFilterQueryValue, Array<any>>,
): string | symbol {
  if (typeof value === 'object' && value.exists) {
    return CATALOG_FILTER_EXISTS;
  }
  return value?.toString();
}

/**
 * Converts schema filters to entity filter query, replacing `{exists:true}` values
 * with the constant `CATALOG_FILTER_EXISTS`.
 *
 * @param schemaFilters - An object containing schema filters with keys as filter names
 * and values as filter values.
 * @returns An object with the same keys as the input object, but with `{exists:true}` values
 * transformed to `CATALOG_FILTER_EXISTS` symbol.
 */
function convertSchemaFiltersToQuery(
  schemaFilters:CBEntityPickerFilterQuery,
): Exclude<EntityFilterQuery, Array<any>> {
  const query: EntityFilterQuery = {};

  for (const [key, value] of Object.entries(schemaFilters)) {
    if (Array.isArray(value)) {
      query[key] = value;
    } else {
      query[key] = convertOpsValues(value);
    }
  }

  return query;
}

/**
 * Builds an `EntityFilterQuery` based on the `uiSchema` passed in.
 * If `catalogFilter` is specified in the `uiSchema`, it is converted to a `EntityFilterQuery`.
 * If `allowedKinds` is specified in the `uiSchema` will support the legacy `allowedKinds` option.
 *
 * @param uiSchema The `uiSchema` of an `EntityPicker` component.
 * @returns An `EntityFilterQuery` based on the `uiSchema`, or `undefined` if `catalogFilter` is not specified in the `uiSchema`.
 */
function buildCatalogFilter(
  uiSchema:CBEntityPickerProps['uiSchema'],
): EntityFilterQuery | undefined {
  const allowedKinds = uiSchema['ui:options']?.allowedKinds;

  const catalogFilter: EntityPickerUiOptions['catalogFilter'] | undefined =
    uiSchema['ui:options']?.catalogFilter ||
    (allowedKinds && { kind: allowedKinds });

  if (!catalogFilter) {
    return undefined;
  }

  if (Array.isArray(catalogFilter)) {
    return catalogFilter.map(convertSchemaFiltersToQuery);
  }

  return convertSchemaFiltersToQuery(catalogFilter);
}